import { useEffect, useRef, useState } from 'react';
import { PIN_MAX_LENGTH, PIN_MIN_LENGTH } from '@abastece/shared';
import { useT } from '../i18n/index.js';
import { Ico } from './Iconos.js';

interface Props {
  onCompleto: (pin: string) => void | Promise<void>;
  ocupado?: boolean;
  error?: string | null;
  /** Reset the entry from outside, after a wrong PIN: the dots shake red, then empty. */
  limpiarToken?: number;
  /** Set once the PIN is right: said on the pad, for a beat, before anything moves on. */
  exito?: string | null;
  /** When that beat is over. Called once. */
  onExito?: () => void;
}

const TECLAS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', 'borrar', '0', 'ok'] as const;

/** How long the greeting stays at rest once it has arrived: long enough to be read. */
const PAUSA_MS = 200;

/** Backstop for a tab that runs no animations: the beat normally ends with its own. */
const ESPERA_MAXIMA_MS = 1200;

/** How long a key stays lit once pressed: its light fades out within it (`.destello`). */
const LUZ_MS = 300;

/**
 * An on-screen keypad rather than a numeric text input.
 *
 * A phone's native keyboard covers half the screen, varies by handset, and on some Android
 * builds will helpfully offer to autocomplete a PIN. This is the same on every device, and the
 * keys are large enough to hit without looking.
 *
 * A real keyboard types on it as well — digits, Backspace, Enter — for the laptop or tablet
 * with one at the counter. It listens on the window instead of waiting to be focused, because
 * a pad that ignores typing until somebody finds the right thing to click looks broken.
 */
export function TecladoPin({
  onCompleto,
  ocupado = false,
  error,
  limpiarToken = 0,
  exito,
  onExito,
}: Props) {
  const t = useT();
  const [pin, setPin] = useState('');
  /** How many dots went red on the last wrong PIN, for as long as they shake. */
  const [fallo, setFallo] = useState(0);
  /**
   * The key just pressed, lit for a moment, and how many presses so far, so a key pressed again
   * lights again (the owner, 2026-10-21: «can we have some visual feedback in the unlock screen
   * when i click the numbers to enter my pin?»). A click is over before the key's own press shows,
   * and a digit typed on a keyboard pressed no key at all; now each lights the key it is, and its
   * dot pops in.
   */
  const [luz, setLuz] = useState<{ tecla: string; vez: number } | null>(null);
  /** Whether the last key added a digit, whose dot pops in; not a digit taken away. */
  const [suma, setSuma] = useState(false);
  const intento = useRef(0);
  const avisado = useRef(false);
  const pausa = useRef<number | undefined>(undefined);
  const raiz = useRef<HTMLDivElement>(null);

  // A sheet closed during the pause must not be authorised by it afterwards.
  useEffect(() => () => window.clearTimeout(pausa.current), []);

  // Out again once its light has faded: with reduced motion it is lit as long, without the fade.
  useEffect(() => {
    if (!luz) return;
    const reloj = window.setTimeout(() => setLuz(null), LUZ_MS);
    return () => window.clearTimeout(reloj);
  }, [luz]);

  // A wrong PIN: the dots that were typed go red and shake the way a head says no, and a phone
  // buzzes, so nobody has to be reading the pad to know.
  useEffect(() => {
    if (limpiarToken === 0) return;
    setFallo(Math.max(intento.current, 1));
    setPin('');
    navigator.vibrate?.([70, 50, 70]);
  }, [limpiarToken]);

  function terminarExito() {
    if (avisado.current) return;
    avisado.current = true;
    onExito?.();
  }

  /** The greeting has arrived: let it sit, then move on. At once for reduced motion. */
  function saludoLlego() {
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) return terminarExito();
    window.clearTimeout(pausa.current);
    pausa.current = window.setTimeout(terminarExito, PAUSA_MS);
  }

  useEffect(() => {
    if (!exito) {
      avisado.current = false;
      return;
    }
    const reloj = window.setTimeout(terminarExito, ESPERA_MAXIMA_MS);
    return () => window.clearTimeout(reloj);
  }, [exito]);

  /*
   * Whatever had focus when the pad came up — the reason typed under a void, the search on the
   * till — would take the digits as well, and a PIN typed into a reason is a PIN written into
   * the record for good. On a phone it also puts away the keyboard that was covering the pad.
   */
  useEffect(() => {
    const activo = document.activeElement;
    if (activo instanceof HTMLElement && !raiz.current?.contains(activo)) activo.blur();
  }, []);

  const pulsar = (tecla: string) => {
    if (ocupado || exito) return;
    if (fallo) setFallo(0);
    setLuz((l) => ({ tecla, vez: (l?.vez ?? 0) + 1 }));
    if (tecla === 'borrar') {
      setSuma(false);
      return setPin((p) => p.slice(0, -1));
    }
    if (tecla === 'ok') {
      if (pin.length >= PIN_MIN_LENGTH) {
        intento.current = pin.length;
        void onCompleto(pin);
      }
      return;
    }
    setSuma(true);
    setPin((p) => (p.length >= PIN_MAX_LENGTH ? p : p + tecla));
  };

  // Attached again after every render, so Enter always sees the digits typed so far.
  useEffect(() => {
    const alTeclear = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.repeat || e.isComposing || e.ctrlKey || e.metaKey || e.altKey) {
        return;
      }
      // A pad that is not taking digits — checking one, or saying hello on its way out — must
      // not claim them either, or they never reach whatever is behind it.
      if (ocupado || exito) return;
      // A pad under the lock screen is not the one being typed on: with the same PIN, Enter
      // would unlock the app and authorise whatever was waiting underneath in one keystroke.
      if (raiz.current?.closest('[inert]')) return;
      const destino = e.target;
      if (
        destino instanceof HTMLElement &&
        (destino.isContentEditable || destino.matches('input, textarea, select'))
      ) {
        return;
      }
      const tecla = /^\d$/.test(e.key)
        ? e.key
        : e.key === 'Backspace'
          ? 'borrar'
          : e.key === 'Enter'
            ? 'ok'
            : null;
      if (!tecla) return;
      // Enter would otherwise also press whichever key was clicked last.
      e.preventDefault();
      pulsar(tecla);
    };
    window.addEventListener('keydown', alTeclear);
    return () => window.removeEventListener('keydown', alTeclear);
  });

  const llenos = exito ? intento.current : fallo || pin.length;
  const color = exito ? 'bg-emerald-400' : fallo ? 'bg-rose-500' : 'bg-marca-500';

  return (
    <div ref={raiz} className="flex w-full max-w-xs flex-col gap-6">
      {/* Keyed by the attempt, so a second wrong PIN shakes again rather than sitting still. */}
      <div
        key={fallo ? `fallo-${limpiarToken}` : 'puntos'}
        className={`flex min-h-8 items-center justify-center gap-3 ${fallo ? 'sacude' : ''}`}
        onAnimationEnd={(e) => e.target === e.currentTarget && setFallo(0)}
      >
        {Array.from({ length: Math.max(PIN_MIN_LENGTH, llenos) }).map((_, i) => {
          // The dot the digit just typed went to, popping in where the eye can see it land.
          const nuevo = suma && !fallo && !exito && i === pin.length - 1;
          return (
            <span
              key={i}
              className={`size-3.5 rounded-full transition ${i < llenos ? color : 'bg-slate-700'} ${
                exito ? 'asiente' : nuevo ? 'punto' : ''
              }`}
              // One after another, and done by the time the message has finished arriving.
              style={exito ? { animationDelay: `${Math.min(i, 5) * 25}ms` } : undefined}
              data-pin-punto={i < llenos ? 'lleno' : 'vacio'}
              data-pin-nuevo={nuevo || undefined}
            />
          );
        })}
      </div>

      <div className="flex min-h-8 items-center justify-center">
        {exito ? (
          <p
            role="status"
            className="brinca inline-flex items-center gap-1.5 rounded-full bg-emerald-500/15 px-3 py-1 text-sm font-medium text-emerald-300 ring-1 ring-emerald-500/30 ring-inset"
            onAnimationEnd={saludoLlego}
          >
            <Ico icono="listo" className="size-4" />
            {exito}
          </p>
        ) : error ? (
          <p
            key={limpiarToken}
            role="alert"
            className="entra inline-flex items-center gap-1.5 rounded-full bg-rose-500/15 px-3 py-1 text-sm font-medium text-rose-300 ring-1 ring-rose-500/30 ring-inset"
          >
            <Ico icono="aviso" className="size-4" />
            {error}
          </p>
        ) : null}
      </div>

      <div className="grid grid-cols-3 gap-3">
        {TECLAS.map((tecla) => (
          <button
            key={tecla}
            type="button"
            disabled={ocupado}
            onClick={() => pulsar(tecla)}
            // Its picture is all it shows: its name, for whoever cannot see it.
            aria-label={tecla === 'borrar' ? t('pin.borrar') : undefined}
            // Pressed further in than an ordinary button, under a finger as under the mouse.
            className={`relative active:scale-95 ${
              tecla === 'ok'
                ? 'btn-primario h-16 text-lg disabled:opacity-40'
                : tecla === 'borrar'
                  ? 'btn-secundario h-16 text-lg'
                  : 'btn-secundario h-16 text-2xl font-semibold'
            }`}
          >
            {/* Its light, behind what it says: the brand's colour, white on «Entrar», already that colour. */}
            {luz?.tecla === tecla && (
              <span
                key={luz.vez}
                aria-hidden
                className={`destello pointer-events-none absolute inset-0 rounded-xl ${tecla === 'ok' ? 'bg-white/30' : 'bg-marca-400/40'}`}
                data-tecla-luz
              />
            )}
            <span className="relative inline-flex items-center gap-2">
              {tecla === 'borrar' ? (
                <Ico icono="retroceso" className="size-6" />
              ) : tecla === 'ok' ? (
                <>
                  <Ico icono="entrar" className="size-5" />
                  {t('comun.entrar')}
                </>
              ) : (
                tecla
              )}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}
